<template>
  <v-container fluid class="kartica">

    <div id="printArea">

      <!-- zaglavlje -->
      <div class="kp-head">
        <div class="kp-firma">
          <div class="kp-firma-ime">{{ comp.CompanyNameShort }}</div>
          <div>{{ comp.CompanyStreet }} {{ comp.CompanyBuildingNameNumber }}</div>
          <div>{{ comp.CompanyPostcode }} {{ comp.CompanyCity }}</div>
          <div>OIB: {{ comp.CompanyVATID }}</div>
        </div>

        <div class="kp-naslov">
          <h2>Kartica partnera</h2>
          <div class="kp-partner">{{ imePartnera }}</div>
          <div class="kp-meta">
            OIB: {{ oibPartnera }}
            <span class="kp-sep">·</span>{{ currency }}
            <span class="kp-sep">·</span>
            {{ head.docFromDate ? localDate(head.docFromDate) : '' }} – {{ head.docToDate ? localDate(head.docToDate) : '' }}
            <span class="kp-sep">·</span>
            konta {{ head.fromAccount }}–{{ head.toAccount }}
          </div>
        </div>

        <div class="kp-akcije">
          <v-btn small outlined color="primary" @click="print">Printaj</v-btn>
          <v-btn small outlined @click="uExcel">Excel</v-btn>
        </div>
      </div>

      <!-- sazetak -->
      <div class="kp-sazetak">
        <div class="kp-kut">
          <span>Početno stanje</span>
          <b :class="{neg: psSaldo < 0}">{{ formatPrice(psSaldo) }}</b>
        </div>
        <div class="kp-kut">
          <span>Promet duguje</span>
          <b>{{ formatPrice(prometD) }}</b>
        </div>
        <div class="kp-kut">
          <span>Promet potražuje</span>
          <b>{{ formatPrice(prometP) }}</b>
        </div>
        <div class="kp-kut kp-istaknuto">
          <span>Saldo na {{ head.docToDate ? localDate(head.docToDate) : 'kraj' }}</span>
          <b :class="{neg: saldoKraj < 0}">{{ formatPrice(saldoKraj) }} {{ saldoKraj >= 0 ? 'D' : 'P' }}</b>
        </div>
        <div class="kp-kut">
          <span>Stavaka</span>
          <b>{{ sveStavke.length }}</b>
        </div>
      </div>

      <!-- tablica -->
      <div class="kp-scroll">
        <table class="kp-tbl">
          <colgroup>
            <col style="width:104px"><col style="width:96px"><col style="width:92px">
            <col style="width:92px"><col style="width:92px"><col style="width:62px">
            <col><col style="width:72px">
            <col style="width:116px"><col style="width:116px"><col style="width:126px">
          </colgroup>
          <thead>
            <tr>
              <th class="l">Dokument</th>
              <th class="l">Veza</th>
              <th class="l">Datum knjiž.</th>
              <th class="l">Datum dok.</th>
              <th class="l">Dospijeće</th>
              <th>Dana</th>
              <th class="l">Opis</th>
              <th class="l">Konto</th>
              <th>Duguje</th>
              <th>Potražuje</th>
              <th>Saldo</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="r in vidljivi" :key="r.kljuc" :class="r.tip">

              <!-- zbirni redak: pocetno stanje ili godina -->
              <template v-if="r.tip !== 'stavka'">
                <td class="naslov" colspan="8">
                  <button
                    v-if="r.stavke.length"
                    type="button"
                    class="strelica"
                    :class="{otvoren: r.open}"
                    :aria-expanded="String(r.open)"
                    @click="r.open = !r.open"
                  >▶</button>
                  {{ r.naziv }}
                  <span class="kp-brojac">{{ r.stavke.length }} stavaka</span>
                </td>
                <td class="num">{{ formatPrice(r.d) }}</td>
                <td class="num">{{ formatPrice(r.p) }}</td>
                <td class="num" :class="{neg: r.saldo < 0}">{{ formatPrice(r.saldo) }}</td>
              </template>

              <!-- redak knjizenja -->
              <template v-else>
                <td class="doc">{{ r.dokument }}</td>
                <td class="doc">{{ r.veza }}</td>
                <td class="doc">{{ r.datum }}</td>
                <td class="doc">{{ r.datumDok }}</td>
                <td class="doc">{{ r.dospijece }}</td>
                <td class="num dana" :class="{kasni: r.dana > 0}">{{ r.dana === null ? '' : r.dana }}</td>
                <td class="opis">{{ r.opis }}</td>
                <td class="doc">{{ r.konto }}</td>
                <td class="num" :class="{nula: !r.debit}">{{ r.debit ? formatPrice(r.debit) : '–' }}</td>
                <td class="num" :class="{nula: !r.credit}">{{ r.credit ? formatPrice(r.credit) : '–' }}</td>
                <td class="num" :class="{neg: r.saldo < 0}">{{ formatPrice(r.saldo) }}</td>
              </template>
            </tr>
          </tbody>
          <tfoot>
            <tr class="uk">
              <td class="naslov" colspan="8">UKUPNO {{ currency }}</td>
              <td class="num">{{ formatPrice(ukupnoD) }}</td>
              <td class="num">{{ formatPrice(ukupnoP) }}</td>
              <td class="num" :class="{neg: saldoKraj < 0}">{{ formatPrice(saldoKraj) }}</td>
            </tr>
          </tfoot>
        </table>
      </div>

      <div class="kp-biljeska">
        „Dana" je razlika između datuma dospijeća i kraja razdoblja. Govori koliko je vremena
        prošlo od dospijeća, a ne znači da stavka nije plaćena — zatvaranje se na ovim kontima
        ne vodi pouzdano.
      </div>

    </div>
  </v-container>
</template>

<script>
// Kartica partnera - sva knjizenja jednog partnera na odabranom rasponu konta.
//
// Pokrece se iz MainLedgerAnalysis gumbom Kartica (type 2). Server vraca retke
// vec filtrirane po partnerVATID i rasponu konta, sortirane po datumu knjizenja.
//
// Dvije stvari koje kartica radi drukcije od sirovog popisa:
//
// 1. Pocetno stanje (knjiga PS) skuplja se u jedan redak. Ono se prenosi iz
//    ranijih godina i zna biti vecina kartice - kod jednog partnera 330 od 826
//    stavaka - pa bi inace zatrpalo prikaz. Redak se moze rasiriti.
// 2. Ostatak se dijeli po godinama, sa medjuzbrojem i saldom na kraju godine.
//
// Saldo se vodi kroz cijelu listu, pocevsi od salda pocetnog stanja.

import ledgerEN from './MainLedgerDescEN'
import ledgerHR from './MainLedgerDescHR'
import commEn from '@/language/commonEN'
import commHr from '@/language/commonHR'
import dateFormat from '@/snippets/dateFormat'
import { testLastUpdate } from '@/services/TestLastUpdate'
import {round} from '@/snippets/allSnippets'
import dayjs from 'dayjs'
import {printPartnerCardPdf} from './mainLedgerPdf'
import {exportPartnerCardXlsx} from './mainLedgerXlsx'
import { domacaValuta } from '@/snippets/domacaValuta'

const KNJIGA_POCETNO_STANJE = 'PS'

export default {
  name: 'MainLedgerPartnerCard',
  props: ['items', 'head', 'showEUR'],
  data () {
    return {
      comp: {},
      lang: {},
      langC: {},
      partners: [],
      partner: {},
      exRate: 1,
      currency: 'EUR',
      sveStavke: [],
      psGrupa: null,
      godine: [],
      psSaldo: 0,
      prometD: 0,
      prometP: 0,
      ukupnoD: 0,
      ukupnoP: 0,
      saldoKraj: 0
    }
  },
  computed: {
    // Redak se vidi samo ako je njegova grupa otvorena.
    vidljivi () {
      const out = []
      if (this.psGrupa) {
        out.push(this.psGrupa)
        if (this.psGrupa.open) { this.psGrupa.stavke.forEach(s => out.push(s)) }
      }
      this.godine.forEach(g => {
        out.push(g)
        if (g.open) { g.stavke.forEach(s => out.push(s)) }
      })
      return out
    },
    imePartnera () {
      // Ime se u knjizenjima zna razlikovati po knjigama (TOS d.o.o. / TOSdoo),
      // pa se uzima iz sifrarnika partnera, a knjizenje sluzi samo kao rezerva.
      if (this.partner && this.partner.partnerName) { return this.partner.partnerName }
      const prvi = this.items && this.items.length ? this.items[0] : null
      return prvi && prvi.partnerName ? prvi.partnerName : ''
    },
    oibPartnera () {
      if (this.partner && this.partner.partnerVATID) { return this.partner.partnerVATID }
      const prvi = this.items && this.items.length ? this.items[0] : null
      return prvi && prvi.partnerVATID ? prvi.partnerVATID : ''
    }
  },
  async mounted () {
    if (this.$store.state.language === 'EN') {
      this.lang = ledgerEN
      this.langC = commEn
    }
    if (this.$store.state.language === 'HR') {
      this.lang = ledgerHR
      this.langC = commHr
    }
    this.exRate = this.$store.state.defExRate ? this.$store.state.defExRate : 1
    if (!this.showEUR) { this.exRate = 1 }
    this.currency = domacaValuta(this.$store.state.businessYear)
    this.comp = this.$store.state.companyActiv ? this.$store.state.companyActiv : {}

    await testLastUpdate()
    this.partners = this.$store.state.partnerStore ? this.$store.state.partnerStore : []
    this.nadjiPartnera()
    this.gradiKarticu()
  },
  methods: {
    formatPrice (value) {
      const n = parseFloat(value)
      const v = isNaN(n) ? 0 : n
      return v.toFixed(2).replace('.', ',').replace(/\B(?=(\d{3})+(?!\d))/g, '.')
    },
    localDate (d) {
      return d ? dateFormat(d) : ''
    },

    // Sifrarnik ima vise zapisa s istim OIB-om, pa se trazi po OIB-u a ne po id-u.
    nadjiPartnera () {
      const prvi = this.items && this.items.length ? this.items[0] : null
      if (!prvi) { return }
      const oib = prvi.partnerVATID
      if (oib) {
        this.partner = this.partners.find(p => p.partnerVATID === oib) || {}
      }
      if (!this.partner.partnerName && prvi.partnerId) {
        this.partner = this.partners.find(p => p.id === parseInt(prvi.partnerId)) || {}
      }
    },

    dokument (item) {
      const book = item.BookName ? String(item.BookName) : ''
      if (book === 'BS') { return 'Izv. ' + (item.BookID ? parseInt(item.BookID) : '') }
      return (book + ' ' + (item.BookID ? parseInt(item.BookID) : '')).trim()
    },
    veza (item) {
      const inNr = item.invoiceInNumber ? String(item.invoiceInNumber).trim() : ''
      const outNr = item.invoiceOutNumber ? String(item.invoiceOutNumber).trim() : ''
      const a2 = item.account2 ? String(item.account2) : ''
      let broj = a2 === '12' ? outNr : inNr
      if (!broj || broj === '0') { broj = outNr && outNr !== '0' ? outNr : (inNr !== '0' ? inNr : '') }
      if (!broj || broj === '0') { return '' }
      return broj + (item.BusinessYearInvoice ? '/' + item.BusinessYearInvoice : '')
    },

    // Opisno, ne tvrdnja o placenosti - koliko je proslo od dospijeca do kraja
    // razdoblja. Zatvaranje se na ovim kontima ne vodi pouzdano pa se ne moze
    // reci sto je jos otvoreno.
    danaOdDospijeca (item) {
      if (!item.InvoicePaymentDeadlineDate || !this.head.docToDate) { return null }
      return dayjs(this.head.docToDate).diff(dayjs(item.InvoicePaymentDeadlineDate), 'day')
    },

    gradiKarticu () {
      const svi = this.items ? [...this.items] : []
      const ps = []
      const ostalo = []

      svi.forEach((item, i) => {
        const debit = round((parseFloat(item.Debit) || 0) / this.exRate, 2)
        const credit = round((parseFloat(item.Credit) || 0) / this.exRate, 2)
        const s = {
          tip: 'stavka',
          kljuc: 's' + i,
          datum: this.localDate(item.BookingDate),
          datumDok: this.localDate(item.InvoiceDate),
          dospijece: this.localDate(item.InvoicePaymentDeadlineDate),
          dana: this.danaOdDospijeca(item),
          dokument: this.dokument(item),
          veza: this.veza(item),
          opis: item.Description || '',
          konto: item.account4 || '',
          debit: debit,
          credit: credit,
          saldo: 0,
          bookingDate: item.BookingDate
        }
        if (String(item.BookName || '') === KNJIGA_POCETNO_STANJE) { ps.push(s) } else { ostalo.push(s) }
      })

      // Pocetno stanje - jedan redak, po potrebi prosiriv.
      const psD = ps.reduce((a, s) => a + s.debit, 0)
      const psP = ps.reduce((a, s) => a + s.credit, 0)
      this.psSaldo = round(psD - psP, 2)
      let saldo = this.psSaldo
      ps.forEach(s => { s.saldo = null })

      this.psGrupa = ps.length
        ? {tip: 'grupa ps', kljuc: 'ps', naziv: 'Početno stanje', stavke: ps,
           d: round(psD, 2), p: round(psP, 2), saldo: this.psSaldo, open: false}
        : null

      // Ostatak po godinama, najstarija prva.
      ostalo.sort((a, b) => (a.bookingDate > b.bookingDate ? 1 : (b.bookingDate > a.bookingDate ? -1 : 0)))
      const poGodini = {}
      ostalo.forEach(s => {
        const g = s.bookingDate ? String(dayjs(s.bookingDate).year()) : '—'
        if (!poGodini[g]) { poGodini[g] = [] }
        saldo = round(saldo + s.debit - s.credit, 2)
        s.saldo = saldo
        poGodini[g].push(s)
      })

      this.godine = Object.keys(poGodini).sort().map(g => {
        const st = poGodini[g]
        return {
          tip: 'grupa godina',
          kljuc: 'g' + g,
          naziv: 'Godina ' + g,
          stavke: st,
          d: round(st.reduce((a, s) => a + s.debit, 0), 2),
          p: round(st.reduce((a, s) => a + s.credit, 0), 2),
          saldo: st.length ? st[st.length - 1].saldo : saldo,
          open: true
        }
      })

      this.prometD = round(ostalo.reduce((a, s) => a + s.debit, 0), 2)
      this.prometP = round(ostalo.reduce((a, s) => a + s.credit, 0), 2)
      this.ukupnoD = round(psD + this.prometD, 2)
      this.ukupnoP = round(psP + this.prometP, 2)
      this.saldoKraj = round(this.ukupnoD - this.ukupnoP, 2)
      this.sveStavke = [...ps, ...ostalo]
    },

    zaIzvoz () {
      return {
        partner: {ime: this.imePartnera, oib: this.oibPartnera},
        psGrupa: this.psGrupa,
        godine: this.godine,
        psSaldo: this.psSaldo,
        prometD: this.prometD,
        prometP: this.prometP,
        ukupnoD: this.ukupnoD,
        ukupnoP: this.ukupnoP,
        saldoKraj: this.saldoKraj,
        brojStavaka: this.sveStavke.length,
        company: this.comp,
        fromDate: this.head.docFromDate,
        toDate: this.head.docToDate,
        fromAccount: this.head.fromAccount,
        toAccount: this.head.toAccount,
        valuta: this.currency
      }
    },
    print () {
      printPartnerCardPdf(this.zaIzvoz())
    },
    uExcel () {
      exportPartnerCardXlsx(this.zaIzvoz())
    }
  }
}
</script>

<style scoped>
.kartica { font-size: 15px; color: #191d1c; }

/* ---------- zaglavlje ---------- */
.kp-head {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1.4fr) auto;
  gap: 20px;
  align-items: start;
  padding: 10px 4px;
  border-bottom: 1px solid #c7cfcb;
}
.kp-firma { line-height: 1.45; color: #4a524f; }
.kp-firma-ime { font-weight: 600; font-size: 15.5px; color: #191d1c; }

.kp-naslov { text-align: center; }
.kp-naslov h2 { font-size: 19px; font-weight: 600; margin: 0 0 2px; }
.kp-partner { font-size: 16px; font-weight: 600; color: #14534c; }
.kp-meta { font-size: 14px; color: #4a524f; }
.kp-sep { color: #79827e; padding: 0 4px; }

.kp-akcije { display: flex; gap: 6px; flex-wrap: wrap; justify-content: flex-end; }

/* ---------- sazetak ---------- */
.kp-sazetak {
  display: flex;
  flex-wrap: wrap;
  gap: 28px;
  padding: 10px 4px;
  border-bottom: 1px solid #e2e6e3;
  background: #fafbfa;
}
.kp-kut { display: flex; flex-direction: column; }
.kp-kut span {
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: .08em;
  color: #79827e;
  font-weight: 600;
}
.kp-kut b { font-size: 17px; font-variant-numeric: tabular-nums; }
.kp-istaknuto b { color: #14534c; }
.kp-kut b.neg { color: #8c2f1e; }

/* ---------- tablica ---------- */
.kp-scroll { overflow: auto; max-height: 62vh; }
.kp-tbl { border-collapse: collapse; width: 100%; }

.kp-tbl thead th {
  position: sticky;
  top: 0;
  background: #fff;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: .06em;
  text-transform: uppercase;
  color: #79827e;
  text-align: right;
  vertical-align: bottom;
  padding: 7px 8px;
  white-space: nowrap;
  border-bottom: 1px solid #c7cfcb;
  z-index: 2;
}
.kp-tbl thead th.l { text-align: left; }

.kp-tbl tbody td,
.kp-tbl tfoot td {
  padding: 3px 8px;
  border-bottom: 1px solid #e2e6e3;
  vertical-align: baseline;
  white-space: nowrap;
}
.kp-tbl td.num {
  text-align: right;
  font-variant-numeric: tabular-nums;
  font-size: 14px;
}
.kp-tbl td.doc { font-size: 13px; color: #4a524f; }
.kp-tbl td.opis { white-space: normal; font-size: 13px; }
.kp-tbl td.nula { color: #b6bdb9; }
.kp-tbl td.neg { color: #8c2f1e; }
.kp-tbl td.dana { color: #79827e; }
.kp-tbl td.dana.kasni { color: #8c2f1e; font-weight: 600; }

.kp-tbl tr.stavka:hover td { background: #f1f5f3; }

.kp-tbl tr.grupa td {
  font-weight: 700;
  border-top: 2px solid #14534c;
  padding-top: 7px;
  padding-bottom: 5px;
  background: #fff;
}
.kp-tbl tr.grupa td.naslov { letter-spacing: .03em; }
.kp-brojac {
  font-weight: 400;
  font-size: 12px;
  color: #79827e;
  padding-left: 10px;
}

.strelica {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 14px;
  height: 14px;
  margin-right: 6px;
  border: 0;
  background: transparent;
  color: #79827e;
  cursor: pointer;
  font-size: 9px;
  line-height: 1;
  padding: 0;
  transition: transform .12s ease;
}
.strelica.otvoren { transform: rotate(90deg); }
.strelica:hover { color: #14534c; }

.kp-tbl tfoot td {
  position: sticky;
  bottom: 0;
  background: #fafbfa;
  font-weight: 700;
  border-top: 2px solid #c7cfcb;
  border-bottom: 0;
}

.kp-biljeska {
  font-size: 12px;
  color: #79827e;
  padding: 8px 4px 0;
  max-width: 78ch;
}

@media (max-width: 900px) {
  .kp-head { grid-template-columns: 1fr; }
  .kp-naslov { text-align: left; }
  .kp-akcije { justify-content: flex-start; }
}
</style>
